/**
 * CheckoutModal — pickup checkout with Square Web Payments.
 *
 *   details  who is picking up, when, and how they'll pay.
 *   payment  the order is quoted with Square first (Square creates the order
 *            and returns the tax-inclusive total), then the buyer pays that
 *            exact order with Apple Pay / Google Pay or a card.
 *   success  confirmation with the pickup code and live order tracking.
 *
 * What keeps money honest here:
 *   • The server prices every line from the Square catalog; the totals shown
 *     on the payment step are Square's, and a moved price is called out.
 *   • Each checkout attempt carries one idempotency key (createAttemptKeys), so
 *     a double tap or a retry returns the same order rather than a second one.
 *   • A quoted, unpaid order is remembered for this tab (savePending). If the
 *     page reloads mid-payment the checkout reopens on that same order, and if
 *     the charge had already landed it goes straight to the confirmation.
 *
 * Environment-aware: the backend decides sandbox vs production and this modal
 * loads the matching Web Payments SDK. Sandbox shows a SANDBOX pill and the
 * test card; production charges real cards and hides both.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useOrdering } from './OrderingContext';
import { loadSquareSdk, getPaymentConfig, quoteOrder, payQuotedOrder, submitOrder, getOrderStatus, fmt$, MONEY } from './orderApi';
import { snapshotOrder, loadContact, saveContact } from './orderHistory';
import {
  createAttemptKeys, cartToItems, quoteDiffers, PICKUP_CHOICES, pickupIso, pickupLabel,
  formatPhoneInput, isValidPhone, savePending, loadPending, clearPending,
} from './orderFlow';
import { useOverlay } from './overlayHooks';
import OrderTracker from './OrderTracker';

const CARD_CONTAINER_ID = 'sq-card-container';
const GPAY_CONTAINER_ID = 'sq-gpay-container';
const KIOSK_RESET_S = 30;

// Square's card fields live in an iframe; this themes them to sit on the dark
// checkout. If a key is ever rejected the card falls back to Square's default.
const CARD_STYLE = {
  '.input-container': { borderColor: '#243d56', borderRadius: '12px' },
  '.input-container.is-focus': { borderColor: '#f59e0b' },
  '.input-container.is-error': { borderColor: '#f87171' },
  '.message-text': { color: '#94a3b8' },
  '.message-icon': { color: '#94a3b8' },
  '.message-text.is-error': { color: '#f87171' },
  '.message-icon.is-error': { color: '#f87171' },
  input: { backgroundColor: '#0d1b2a', color: '#e2e8f0', fontSize: '16px' },
  'input::placeholder': { color: '#64748b' },
  'input.is-error': { color: '#f87171' },
};

const cartSig = (items) => JSON.stringify(cartToItems(items));

export default function CheckoutModal() {
  const {
    canOrder, kiosk, cart, nameMap, checkoutOpen, setCheckoutOpen, setDrawerOpen, payEnv,
    recordOrder, updateOrder, setPaying, resetSession, cartDocked,
  } = useOrdering();
  const isSandbox = payEnv !== 'production';
  const [stage, setStage] = useState('details');   // details | payment | success
  const [busy, setBusy] = useState(false);          // pricing, placing or charging
  const [form, setForm] = useState('idle');         // idle | loading | ready | failed — the card form
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [pickupName, setPickupName] = useState('');
  const [pickupPhone, setPickupPhone] = useState('');
  const [pickupChoice, setPickupChoice] = useState('asap');
  const [customTime, setCustomTime] = useState('');
  const [orderNote, setOrderNote] = useState('');
  // Paying now is the encouraged path — prepaid orders flow straight into
  // Square and don't no-show. The kiosk defaults to paying at the window:
  // nobody should have to type a card number on a shared tablet.
  const [payMethod, setPayMethod] = useState(kiosk ? 'store' : 'card'); // card | store
  const [quote, setQuote] = useState(null);     // { orderId, totalMoney, taxMoney, pickupAt, lines }
  const [appleReady, setAppleReady] = useState(false);
  const [gpayReady, setGpayReady] = useState(false);
  const [resetIn, setResetIn] = useState(KIOSK_RESET_S);

  const keys = useRef(createAttemptKeys());
  const cardRef = useRef(null);
  const gpayRef = useRef(null);
  const appleRef = useRef(null);
  // Set when a reload-interrupted checkout is restored: `resumed` stops the
  // open handler from resetting it, `checkPaid` asks Square whether it was paid.
  const resumed = useRef(false);
  const checkPaid = useRef(false);

  const open = !!(canOrder && checkoutOpen);
  const resolveName = useCallback((menuName) => {
    const r = nameMap?.resolve?.(menuName);
    return r && r.source !== 'none' ? r.squareName : null;
  }, [nameMap]);

  // Shared success path: snapshot into device order history (before the cart
  // is cleared) and remember the contact for next time's prefill.
  const finishOrder = useCallback((res, payAtStore) => {
    recordOrder?.(snapshotOrder({ cartItems: cart.items, result: res, payAtStore, pickupName: pickupName.trim() }));
    if (!kiosk) saveContact({ name: pickupName.trim(), phone: pickupPhone.trim() });
    clearPending();
    keys.current.clear();
    setResult({ ...res, payAtStore });
    setErr('');
    setResetIn(KIOSK_RESET_S);
    setStage('success');
    cart.clear();
  }, [cart, kiosk, pickupName, pickupPhone, recordOrder]);

  // ── Resume a checkout the page was reloaded out of (never on the kiosk) ────
  useEffect(() => {
    if (kiosk) return;
    const p = loadPending();
    if (!p) return;
    if (p.cartSig !== cartSig(cart.items)) { clearPending(); return; }
    const d = p.details || {};
    setPickupName(d.pickupName || ''); setPickupPhone(d.pickupPhone || '');
    setPickupChoice(d.pickupChoice || 'asap'); setCustomTime(d.customTime || ''); setOrderNote(d.orderNote || '');
    setQuote(p.quote);
    setStage('payment');
    resumed.current = true;
    checkPaid.current = true;
    setCheckoutOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If that order was in fact paid before the reload, there is nothing to pay.
  useEffect(() => {
    if (stage !== 'payment' || !quote?.orderId || !checkPaid.current) return;
    checkPaid.current = false;
    getOrderStatus(quote.orderId)
      .then(st => { if (st.paid) finishOrder({ orderId: quote.orderId, totalMoney: quote.totalMoney, status: 'COMPLETED' }, false); })
      .catch(() => { /* the payment step still works without this check */ });
  }, [stage, quote, finishOrder]);

  // ── Fresh open ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!checkoutOpen) return;
    if (resumed.current) { resumed.current = false; return; }
    setStage('details'); setErr(''); setResult(null); setQuote(null); setBusy(false);
    setPayMethod(kiosk ? 'store' : 'card');
    if (kiosk) {
      setPickupName(''); setPickupPhone(''); setOrderNote(''); setPickupChoice('asap'); setCustomTime('');
    } else {
      // Prefill pickup details from the last order on this device.
      const c = loadContact();
      if (c.name) setPickupName(c.name);
      if (c.phone) setPickupPhone(formatPhoneInput(c.phone));
    }
  }, [checkoutOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    if (busy) return;
    // Leaving on purpose: don't reopen this checkout on the next page load.
    if (stage === 'payment') clearPending();
    if (stage === 'success' && kiosk) { resetSession?.(); return; }
    setCheckoutOpen(false);
  }, [busy, stage, kiosk, resetSession, setCheckoutOpen]);
  useOverlay(open, close);

  const editCart = () => { setCheckoutOpen(false); if (!cartDocked) setDrawerOpen(true); };
  const backToDetails = () => { if (busy) return; clearPending(); setQuote(null); setErr(''); setStage('details'); };

  const validateDetails = () => {
    if (!cart.items.length) return 'Your cart is empty.';
    if (!pickupName.trim()) return 'Please enter a name for pickup.';
    if (!pickupPhone.trim()) return 'Please enter a phone number so the truck can reach you about your order.';
    if (!isValidPhone(pickupPhone)) return 'That phone number looks invalid — check the digits and try again.';
    if (pickupChoice === 'custom') {
      const at = pickupIso('custom', customTime);
      if (!at) return 'Choose a pickup time, or pick ASAP.';
      if (new Date(at).getTime() < Date.now() + 10 * 60 * 1000) return 'Pick a time at least 10 minutes from now, or choose ASAP.';
    }
    return null;
  };

  /**
   * The request for this attempt. The idempotency key follows what the customer
   * chose, not the clock: a relative pickup ("30 min") is timed from when the
   * attempt started, so sending it again produces the identical order.
   */
  const buildRequest = (payAtStore) => {
    const chosen = {
      items: cartToItems(cart.items, resolveName),
      pickupName: pickupName.trim(),
      pickupPhone: pickupPhone.trim(),
      note: orderNote.trim() || null,
    };
    const attempt = keys.current.keyFor({ ...chosen, pickupChoice, customTime, payAtStore });
    return {
      ...chosen,
      pickupTime: pickupIso(pickupChoice, customTime, new Date(attempt.requestedAt)),
      ...attempt,
      ...(payAtStore ? { payAtStore: true } : {}),
    };
  };

  const failAttempt = (e, fallback) => {
    // Square refused to replay this key with what we sent — start a new attempt.
    if (e?.code === 'IDEMPOTENCY_KEY_REUSED') keys.current.clear();
    setErr(e?.message || fallback);
  };

  // Pay at pickup: create the unpaid order; staff take payment at the truck.
  const placeStoreOrder = async () => {
    const invalid = validateDetails();
    if (invalid) { setErr(invalid); return; }
    setErr(''); setBusy(true);
    try {
      finishOrder(await submitOrder(buildRequest(true)), true);
    } catch (e) {
      failAttempt(e, 'Could not place the order');
    } finally { setBusy(false); }
  };

  // details → payment: have Square create and price the order.
  const goToPayment = async () => {
    const invalid = validateDetails();
    if (invalid) { setErr(invalid); return; }
    setErr(''); setBusy(true);
    try {
      const q = await quoteOrder(buildRequest(false));
      setQuote(q);
      setStage('payment');
      if (!kiosk) {
        savePending({
          quote: { orderId: q.orderId, totalMoney: q.totalMoney, taxMoney: q.taxMoney, pickupAt: q.pickupAt, lines: q.lines },
          details: { pickupName, pickupPhone, pickupChoice, customTime, orderNote },
          cartSig: cartSig(cart.items),
        });
      }
    } catch (e) {
      failAttempt(e, 'Could not start checkout');
    } finally { setBusy(false); }
  };

  // ── Payment step: mount Square's card form and any available wallets ──────
  const quotedOrderId = quote?.orderId;
  const quotedCents = quote?.totalMoney?.amount;
  useEffect(() => {
    if (!open || stage !== 'payment' || !quotedOrderId) return undefined;
    let cancelled = false;
    const mounted = [];
    setForm('loading'); setAppleReady(false); setGpayReady(false);

    (async () => {
      try {
        const cfg = await getPaymentConfig();
        const Square = await loadSquareSdk(cfg.environment);
        if (cancelled) return;
        const payments = Square.payments(cfg.appId, cfg.locationId);

        let card;
        try { card = await payments.card({ style: CARD_STYLE }); }
        catch { card = await payments.card(); }
        mounted.push(card);
        if (cancelled) return;
        await card.attach(`#${CARD_CONTAINER_ID}`);
        if (cancelled) return;
        cardRef.current = card;
        setForm('ready');

        // Wallets are best-effort — each needs a supporting browser/device.
        // The sheet shows this total, so it is in dollars ("24.76"), not cents.
        if (!quotedCents) return;
        const request = payments.paymentRequest({
          countryCode: 'US', currencyCode: 'USD',
          total: { amount: (quotedCents / 100).toFixed(2), label: 'Desi Bites' },
        });
        try {
          const gp = await payments.googlePay(request);
          mounted.push(gp);
          if (cancelled) return;
          await gp.attach(`#${GPAY_CONTAINER_ID}`);
          if (cancelled) return;
          gpayRef.current = gp;
          setGpayReady(true);
        } catch { /* Google Pay unavailable */ }
        try {
          const ap = await payments.applePay(request);
          mounted.push(ap);
          if (cancelled) return;
          appleRef.current = ap;
          setAppleReady(true);
        } catch { /* Apple Pay unavailable (non-Safari / domain not registered) */ }
      } catch (e) {
        if (!cancelled) { setForm('failed'); setErr(e.message || 'Could not load the payment form'); }
      }
    })();

    return () => {
      cancelled = true;
      mounted.forEach(m => { try { m.destroy?.(); } catch { /* already gone */ } });
      cardRef.current = null; gpayRef.current = null; appleRef.current = null;
    };
  }, [open, stage, quotedOrderId, quotedCents]);

  // Charge the quoted order with whatever token we just tokenized.
  const charge = async (sourceId) => {
    setBusy(true); setPaying?.(true); setErr('');
    try {
      const res = await payQuotedOrder({ orderId: quote.orderId, sourceId, pickupName: pickupName.trim() });
      finishOrder({ ...res, pickupAt: quote.pickupAt }, false);
    } catch (e) {
      setErr(e.message || 'Payment failed');
    } finally { setBusy(false); setPaying?.(false); }
  };

  const payCard = async () => {
    if (busy) return;
    if (!cardRef.current) { setErr('The card form is still loading — one moment.'); return; }
    setErr('');
    try {
      const tok = await cardRef.current.tokenize();
      if (tok.status !== 'OK') { setErr(tok.errors?.[0]?.message || 'Check the card details and try again.'); return; }
      await charge(tok.token);
    } catch (e) { setErr(e.message || 'Payment failed'); }
  };

  // Wallet buttons must tokenize inside the tap that opened them.
  const payWallet = async (wallet, label) => {
    if (busy || !wallet) return;
    setErr('');
    try {
      const r = await wallet.tokenize();
      if (r.status === 'OK') await charge(r.token);
      else if (r.status !== 'Cancel') setErr(r.errors?.[0]?.message || `${label} didn't go through — try a card instead.`);
    } catch (e) { setErr(e.message || `${label} failed`); }
  };

  // Kiosk: the confirmation clears itself for the next customer.
  useEffect(() => {
    if (!open || !kiosk || stage !== 'success') return undefined;
    const t = setInterval(() => setResetIn(n => n - 1), 1000);
    return () => clearInterval(t);
  }, [open, kiosk, stage]);
  useEffect(() => {
    if (open && kiosk && stage === 'success' && resetIn <= 0) resetSession?.();
  }, [open, kiosk, stage, resetIn, resetSession]);

  const itemCount = useMemo(() => cart.items.reduce((s, it) => s + it.qty, 0), [cart.items]);

  if (!open) return null;

  const total = quote?.totalMoney ? MONEY(quote.totalMoney.amount) : fmt$(cart.subtotal);
  const priceMoved = stage === 'payment' && quoteDiffers(cart.items, quote);
  const steps = payMethod === 'card' ? ['Details', 'Payment', 'Done'] : ['Details', 'Done'];
  const stepIdx = stage === 'success' ? steps.length - 1 : stage === 'payment' ? 1 : 0;
  const lines = stage === 'payment' && quote?.lines
    ? quote.lines.map((l, i) => ({ key: i, label: `${l.qty}× ${l.name}`, amount: MONEY(l.qty * l.unitCents) }))
    : cart.items.map(it => ({
      key: it.key,
      label: `${it.qty}× ${it.name}${it.variationName && it.variationName !== 'Regular' ? ` (${it.variationName})` : ''}`,
      amount: fmt$(it.qty * Number(it.unitPrice ?? it.basePrice ?? it.price ?? 0)),
    }));
  const code = result?.orderId ? String(result.orderId).slice(-6).toUpperCase() : '';

  return (
    <div className="ord-overlay" onClick={close}>
      <div className="ord-modal cko-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Checkout">
        <div className="ord-drawer__head">
          <h3>
            {stage === 'payment' && (
              <button className="ord-x ord-x--back" onClick={backToDetails} aria-label="Back to details" disabled={busy}>←</button>
            )}
            {stage === 'success' ? 'Order confirmed' : 'Checkout'}
            {isSandbox && <span className="ord-test-pill">SANDBOX</span>}
          </h3>
          <button className="ord-x" onClick={close} aria-label="Close" disabled={busy}>✕</button>
        </div>

        {stage !== 'success' && (
          <div className="cko-steps" aria-hidden="true">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <span className={`cko-steps__bar${i <= stepIdx ? ' cko-steps__bar--on' : ''}`} />}
                <span className={`cko-steps__dot${i <= stepIdx ? ' cko-steps__dot--on' : ''}${i === stepIdx ? ' cko-steps__dot--current' : ''}`}>
                  <span className="cko-steps__num">{i < stepIdx ? '✓' : i + 1}</span>
                  <span className="cko-steps__label">{s}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {stage === 'success' ? (
          <div className="ord-success">
            <div className="ord-success__icon">
              <span className="ord-success__ring" />
              <span className="ord-success__check">✓</span>
            </div>
            <h4>Thanks{pickupName.trim() ? `, ${pickupName.trim().split(/\s+/)[0]}` : ''}!</h4>
            <p className="ord-success__msg">
              {result?.payAtStore
                ? <>Pay <strong>{result?.totalMoney ? MONEY(result.totalMoney.amount) : ''}</strong> when you pick up.</>
                : <>We charged <strong>{result?.totalMoney ? MONEY(result.totalMoney.amount) : ''}</strong>. Nothing more to pay.</>}
            </p>

            <div className="ord-success__code">
              <span>Pickup code</span>
              <strong>#{code}</strong>
              <small>Give your name or this code at the window.</small>
            </div>

            {kiosk ? (
              <p className="ord-success__note">
                We'll call <strong>{pickupName.trim() || 'your name'}</strong> when it's ready
                {result?.pickupAt ? <> — around <strong>{pickupLabel(result.pickupAt)}</strong></> : null}.
              </p>
            ) : (
              result?.orderId && <OrderTracker orderId={result.orderId} onChange={st => updateOrder?.(result.orderId, { stage: st.stage, done: st.stage === 'picked_up' || st.stage === 'canceled' })} />
            )}

            {!kiosk && pickupPhone && <p className="ord-success__note">📞 We'll call {pickupPhone} if anything changes.</p>}
            {result?.receiptUrl && (
              <a className="ord-btn ord-btn--ghost" href={result.receiptUrl} target="_blank" rel="noopener noreferrer">View receipt</a>
            )}
            <button className="ord-btn ord-btn--primary" onClick={close}>
              {kiosk ? `Start a new order (${Math.max(0, resetIn)})` : 'Done'}
            </button>
          </div>
        ) : (
          <div className="ord-modal__body">
            {/* Order summary */}
            <div className="cko-summary">
              <div className="cko-summary__head">
                <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                {stage === 'details' && <button className="cko-summary__edit" onClick={editCart}>Edit cart</button>}
              </div>
              <div className="cko-summary__lines">
                {lines.map(l => (
                  <div key={l.key} className="ord-summary__row"><span>{l.label}</span><span>{l.amount}</span></div>
                ))}
              </div>
              {stage === 'payment' && quote?.taxMoney && (
                <div className="ord-summary__row ord-summary__row--sub">
                  <span>Tax</span><span>{MONEY(quote.taxMoney.amount)}</span>
                </div>
              )}
              <div className="ord-summary__total">
                <span>{stage === 'payment' ? 'Total' : 'Subtotal'}</span>
                <strong>{total}</strong>
              </div>
              {stage === 'details' && <p className="ord-note">Tax is added on the next step.</p>}
              {priceMoved && <p className="cko-moved">A price changed since you added these items — this is today's menu price.</p>}
            </div>

            {stage === 'details' ? (
              <>
                <label className="cko-field">
                  <span className="cko-field__label">Name for the order</span>
                  <span className="cko-field__box">
                    <input value={pickupName} onChange={e => setPickupName(e.target.value)} placeholder="Your name"
                      autoComplete={kiosk ? 'off' : 'name'} maxLength={60} />
                  </span>
                </label>
                <label className="cko-field">
                  <span className="cko-field__label">Mobile number</span>
                  <span className="cko-field__box">
                    <input
                      type="tel" inputMode="tel" value={pickupPhone}
                      onChange={e => setPickupPhone(formatPhoneInput(e.target.value))}
                      placeholder="(479) 555-1234" autoComplete={kiosk ? 'off' : 'tel'} required />
                  </span>
                  <small className="ord-field__hint">Only used if the truck needs to reach you about this order.</small>
                </label>

                <div className="cko-field">
                  <span className="cko-field__label">Pickup time</span>
                  <div className="cko-chips" role="radiogroup" aria-label="Pickup time">
                    {PICKUP_CHOICES.map(c => (
                      <button
                        key={c.id} type="button" role="radio" aria-checked={pickupChoice === c.id}
                        className={`cko-chip${pickupChoice === c.id ? ' cko-chip--on' : ''}`}
                        onClick={() => setPickupChoice(c.id)}>
                        {c.label}{c.hint && <small>{c.hint}</small>}
                      </button>
                    ))}
                  </div>
                  {pickupChoice === 'custom' && (
                    <span className="cko-field__box cko-field__box--gap">
                      <input type="datetime-local" value={customTime} onChange={e => setCustomTime(e.target.value)} aria-label="Pickup date and time" />
                    </span>
                  )}
                </div>

                <label className="cko-field">
                  <span className="cko-field__label">Note <em>optional</em></span>
                  <span className="cko-field__box">
                    <input value={orderNote} onChange={e => setOrderNote(e.target.value)} placeholder="Allergy, parking spot…" maxLength={200} />
                  </span>
                </label>

                <div className="cko-field__label">Payment</div>
                <div className="ord-paychoice" role="radiogroup" aria-label="Payment method">
                  <button
                    type="button"
                    className={`ord-paychoice__opt${payMethod === 'card' ? ' ord-paychoice__opt--on' : ''}`}
                    onClick={() => setPayMethod('card')} role="radio" aria-checked={payMethod === 'card'}>
                    <span className="ord-paychoice__radio" aria-hidden="true" />
                    <span className="ord-paychoice__icon">💳</span>
                    <span>
                      <strong>Pay now <em className="ord-paychoice__tag">Fastest</em></strong>
                      <small>Card, Apple Pay or Google Pay</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`ord-paychoice__opt${payMethod === 'store' ? ' ord-paychoice__opt--on' : ''}`}
                    onClick={() => setPayMethod('store')} role="radio" aria-checked={payMethod === 'store'}>
                    <span className="ord-paychoice__radio" aria-hidden="true" />
                    <span className="ord-paychoice__icon">💵</span>
                    <span><strong>Pay at pickup</strong><small>Card or cash at the window</small></span>
                  </button>
                </div>

                {err && <div className="ord-err" role="alert">⚠ {err}</div>}
                <button
                  className="ord-btn ord-btn--primary ord-btn--pay"
                  onClick={payMethod === 'store' ? placeStoreOrder : goToPayment}
                  disabled={busy}>
                  {busy
                    ? (payMethod === 'store' ? 'Placing your order…' : 'Getting your total…')
                    : (payMethod === 'store' ? `Place order · ${fmt$(cart.subtotal)} + tax` : 'Continue to payment')}
                </button>
              </>
            ) : (
              <>
                {/* Express wallets — only the ones this device supports render */}
                <div className={`ord-wallets${appleReady || gpayReady ? '' : ' ord-wallets--none'}`}>
                  {appleReady && (
                    <button className="ord-applepay" onClick={() => payWallet(appleRef.current, 'Apple Pay')} disabled={busy} aria-label="Pay with Apple Pay">
                       Pay
                    </button>
                  )}
                  {/* Square draws the Google Pay button in here; the tap is ours to handle. */}
                  <div id={GPAY_CONTAINER_ID} className="ord-gpay" onClick={() => payWallet(gpayRef.current, 'Google Pay')} />
                </div>
                {(appleReady || gpayReady) && <div className="ord-or"><span>or pay with card</span></div>}

                <div className="ord-field">
                  {form === 'loading' && <div className="ord-card-loading">Loading secure card form…</div>}
                  {/* Square owns everything inside this node — React renders nothing into it. */}
                  <div id={CARD_CONTAINER_ID} className="ord-card-box" />
                  {isSandbox && <p className="ord-test-card">Test card: <code>4111 1111 1111 1111</code> · any future date · any CVV · any ZIP</p>}
                </div>

                {err && <div className="ord-err" role="alert">⚠ {err}</div>}

                <button className="ord-btn ord-btn--primary ord-btn--pay" onClick={payCard} disabled={busy || form !== 'ready'}>
                  {busy ? 'Processing…' : `Pay ${total}`}
                </button>
                <p className="cko-secure">🔒 Card details go straight to Square — we never see or store them.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
